"use client";

import { Button } from "@relume_io/relume-ui";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { RxChevronRight } from "react-icons/rx";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const TabItem = ({ tabItem, index, activeTab }) => {
  if (index !== activeTab) {
    return null;
  }
  return (
    <motion.div
      initial={{ opacity: 0 }}
      exit={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="relative flex items-center justify-center w-full h-full min-h-[400px]"
    >
      {tabItem.image && (
        <img
          src={tabItem.image.src}
          alt={tabItem.image.alt}
          className="w-full h-full object-cover rounded-lg"
        />
      )}
    </motion.div>
  );
};

const useRelume = () => {
  const [activeTab, setActiveTab] = useState(0);
  const setActiveTabSetter = (index) => () => setActiveTab(index);
  const getActiveTabButtonStyles = (index) => {
    return cn("cursor-pointer border-b border-border-primary py-4 transition-opacity duration-300", {
      "opacity-100": activeTab === index,
      "opacity-40": activeTab !== index,
    });
  };
  const getActiveTabButtonContentStyles = (index) => {
    return {
      height: activeTab === index ? "auto" : 0,
      opacity: activeTab === index ? 1 : 0,
    };
  };
  return {
    setActiveTabSetter,
    getActiveTabButtonStyles,
    getActiveTabButtonContentStyles,
    activeTab,
  };
};

export function Layout494() {
  const useActive = useRelume();
  return (
    <section id="relume" className="px-[5%] py-16 md:py-24 lg:py-28">
      <div className="container">
        <div className="relative flex flex-col md:flex-row">
          <div className="w-full md:w-1/2 md:pr-6 lg:pr-10">
            <div className="mb-8 md:hidden">
              <p className="mb-3 font-semibold md:mb-4">Tagline</p>
              <h1 className="mb-5 text-5xl font-bold md:mb-6 md:text-7xl lg:text-8xl">
                One click, no disruption
              </h1>
              <p className="md:text-md">
                The AI Loader brings your data in with a single click. It
                preserves relationships, formulas, and context wherever
                possible.
              </p>
            </div>
            <AnimatePresence initial={false} mode="wait">
              <TabItem
                tabItem={{
                  heading: "No more copy-paste",
                  description: "Your data moves between tools while staying intact.",
                  image: {
                    src: "/images/layouts/tool-integration.png",
                    alt: "Tool integration visualization",
                  },
                }}
                index={0}
                activeTab={useActive.activeTab}
              />
              <TabItem
                tabItem={{
                  heading: "Preserved context",
                  description: "Relationships and formulas stay connected.",
                  image: {
                    src: "/images/layouts/ai-insights.png",
                    alt: "AI insights visualization",
                  },
                }}
                index={1}
                activeTab={useActive.activeTab}
              />
              <TabItem
                tabItem={{
                  heading: "Seamless sync",
                  description: "Information keeps its meaning as it moves.",
                  image: {
                    src: "/images/layouts/spine-concept.png",
                    alt: "Spine concept visualization",
                  },
                }}
                index={2}
                activeTab={useActive.activeTab}
              />
            </AnimatePresence>
          </div>
          <div className="w-full md:w-1/2 md:pl-6 lg:pl-10">
            <div className="mb-8 hidden md:block">
              <p className="mb-3 font-semibold md:mb-4">Seamless</p>
              <h1 className="mb-5 text-5xl font-bold md:mb-6 md:text-7xl lg:text-8xl">
                One click, no disruption
              </h1>
              <p className="md:text-md">
                The AI Loader brings your data in with a single click. It
                preserves relationships, formulas, and context wherever
                possible, so your information keeps its meaning as it moves.
              </p>
            </div>
            <div className="static flex flex-col flex-wrap justify-stretch md:block">
              <div className="relative grid auto-cols-fr grid-cols-1 grid-rows-[auto_auto] items-start md:mb-0 md:items-stretch">
                <div
                  onClick={useActive.setActiveTabSetter(0)}
                  className={useActive.getActiveTabButtonStyles(0)}
                >
                  <h2 className="text-xl font-bold md:text-2xl">
                    No more copy-paste
                  </h2>
                  <motion.div
                    initial={false}
                    animate={useActive.getActiveTabButtonContentStyles(0)}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="mt-2">
                      Your data moves between tools while staying intact and
                      connected.
                    </p>
                  </motion.div>
                </div>
                <div
                  onClick={useActive.setActiveTabSetter(1)}
                  className={useActive.getActiveTabButtonStyles(1)}
                >
                  <h2 className="text-xl font-bold md:text-2xl">
                    Preserved context
                  </h2>
                  <motion.div
                    initial={false}
                    animate={useActive.getActiveTabButtonContentStyles(1)}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="mt-2">
                      Relationships and formulas stay connected across tools.
                    </p>
                  </motion.div>
                </div>
                <div
                  onClick={useActive.setActiveTabSetter(2)}
                  className={useActive.getActiveTabButtonStyles(2)}
                >
                  <h2 className="text-xl font-bold md:text-2xl">
                    Seamless sync
                  </h2>
                  <motion.div
                    initial={false}
                    animate={useActive.getActiveTabButtonContentStyles(2)}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="mt-2">
                      Information keeps its meaning as it moves.
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 md:mt-8">
              <Button title="See" variant="secondary">
                See
              </Button>
              <Button
                title="Arrow"
                variant="link"
                size="link"
                iconRight={<RxChevronRight />}
              >
                Arrow
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
